import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Typography, Button, Slider } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import MicIcon from "@material-ui/icons/Mic";
import VideocamIcon from "@material-ui/icons/Videocam";
import AspectRatioIcon from "@material-ui/icons/AspectRatio";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import GetAppIcon from "@material-ui/icons/GetApp";
import { AudioPlayerContext } from "../../context/AudioPlayer/AudioPlayerContext";

const MIN_VIDEO_W = 280;
const MIN_VIDEO_H = 200;
const DEFAULT_VIDEO_W = 480;
const DEFAULT_VIDEO_H = 340;

const useStyles = makeStyles((theme) => ({
	// ===================== AUDIO PLAYER =====================
	audioRoot: {
		position: "fixed",
		bottom: 16,
		right: 16,
		zIndex: 9999,
		backgroundColor: theme.palette.background.paper,
		borderRadius: 12,
		boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
		padding: "8px 12px",
		display: "flex",
		alignItems: "center",
		gap: 8,
		minWidth: 280,
		maxWidth: 380,
		border: `1px solid ${theme.palette.divider}`,
	},
	micIcon: {
		color: theme.palette.primary.main,
		fontSize: 20,
		marginRight: 2,
	},
	contactName: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		maxWidth: 100,
	},
	timeText: {
		fontSize: "0.7rem",
		color: theme.palette.text.secondary,
		minWidth: 70,
		textAlign: "center",
	},
	slider: {
		flex: 1,
		marginLeft: 4,
		marginRight: 4,
	},
	rateButton: {
		fontSize: "0.7rem",
		minWidth: "auto",
		padding: "2px 6px",
		height: 22,
		borderRadius: 11,
		textTransform: "none",
		backgroundColor: "rgba(0,0,0,0.08)",
	},
	closeButton: {
		padding: 4,
	},

	// ===================== VIDEO PLAYER =====================
	videoRoot: {
		position: "fixed",
		zIndex: 9999,
		borderRadius: 10,
		boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#111",
		border: "1px solid rgba(255,255,255,0.08)",
	},

	// --- Header (drag bar) ---
	videoHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "5px 10px",
		background: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.65) 100%)",
		cursor: "move",
		userSelect: "none",
		minHeight: 34,
		flexShrink: 0,
		borderBottom: "1px solid rgba(255,255,255,0.06)",
	},
	videoHeaderLeft: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		overflow: "hidden",
		flex: 1,
	},
	videoHeaderIcon: {
		color: "#fff",
		fontSize: 16,
		opacity: 0.7,
	},
	videoHeaderName: {
		fontSize: "0.78rem",
		color: "rgba(255,255,255,0.85)",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		flex: 1,
		fontWeight: 500,
	},
	videoHeaderActions: {
		display: "flex",
		alignItems: "center",
		gap: 1,
	},
	headerBtn: {
		color: "rgba(255,255,255,0.65)",
		padding: 4,
		transition: "color 0.15s, background 0.15s",
		"&:hover": {
			color: "#fff",
			backgroundColor: "rgba(255,255,255,0.1)",
		},
	},
	headerBtnClose: {
		color: "rgba(255,255,255,0.65)",
		padding: 4,
		transition: "color 0.15s, background 0.15s",
		"&:hover": {
			color: "#fff",
			backgroundColor: "rgba(255,60,60,0.35)",
		},
	},

	// --- Video body ---
	videoBody: {
		position: "relative",
		flex: 1,
		minHeight: 0,
		backgroundColor: "#000",
		overflow: "hidden",
		cursor: "pointer",
		"&:hover $videoControlsOverlay": {
			opacity: 1,
		},
	},
	videoElement: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		display: "block",
		objectFit: "contain",
	},

	// --- Big play overlay ---
	videoBigPlay: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: 60,
		height: 60,
		borderRadius: "50%",
		backgroundColor: "rgba(0,0,0,0.5)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		transition: "transform 0.2s ease, background-color 0.2s ease",
		backdropFilter: "blur(6px)",
		zIndex: 4,
		"&:hover": {
			transform: "translate(-50%, -50%) scale(1.1)",
			backgroundColor: "rgba(0,0,0,0.65)",
		},
		"& svg": {
			color: "#fff",
			fontSize: 34,
			marginLeft: 3,
		},
	},

	// --- Controls overlay ---
	videoControlsOverlay: {
		position: "absolute",
		bottom: 0,
		left: 0,
		right: 0,
		background: "linear-gradient(transparent, rgba(0,0,0,0.80))",
		opacity: 0,
		transition: "opacity 0.25s ease",
		zIndex: 5,
		display: "flex",
		flexDirection: "column",
		padding: "20px 10px 8px 10px",
	},
	videoProgressRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		padding: "0 2px",
		marginBottom: 2,
	},
	videoProgressSlider: {
		flex: 1,
		color: "#fff",
		padding: "6px 0",
		"& .MuiSlider-rail": {
			height: 3,
			borderRadius: 2,
			opacity: 0.35,
			backgroundColor: "rgba(255,255,255,0.4)",
		},
		"& .MuiSlider-track": {
			height: 3,
			borderRadius: 2,
		},
		"& .MuiSlider-thumb": {
			width: 14,
			height: 14,
			marginTop: -5.5,
			backgroundColor: "#fff",
			"&:hover, &.Mui-focusVisible": {
				boxShadow: "0 0 0 6px rgba(255,255,255,0.2)",
			},
		},
	},
	videoTimeText: {
		color: "rgba(255,255,255,0.85)",
		fontSize: "0.72rem",
		fontVariantNumeric: "tabular-nums",
		minWidth: 38,
		textAlign: "center",
		userSelect: "none",
		whiteSpace: "nowrap",
	},
	videoControlsRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "0 2px",
	},
	videoControlsLeft: {
		display: "flex",
		alignItems: "center",
		gap: 2,
	},
	videoControlsRight: {
		display: "flex",
		alignItems: "center",
		gap: 2,
	},
	controlBtn: {
		color: "#fff",
		padding: 5,
		transition: "background 0.15s",
		"&:hover": {
			backgroundColor: "rgba(255,255,255,0.15)",
		},
		"& svg": {
			fontSize: 22,
		},
	},

	// --- Resize handles ---
	resizeHandle: {
		position: "absolute",
		width: 20,
		height: 20,
		bottom: 0,
		right: 0,
		cursor: "nwse-resize",
		zIndex: 10,
		"&::after": {
			content: '""',
			position: "absolute",
			bottom: 3,
			right: 3,
			width: 8,
			height: 8,
			borderRight: "2px solid rgba(255,255,255,0.35)",
			borderBottom: "2px solid rgba(255,255,255,0.35)",
		},
	},
	resizeHandleBL: {
		position: "absolute",
		width: 20,
		height: 20,
		bottom: 0,
		left: 0,
		cursor: "nesw-resize",
		zIndex: 10,
	},
	resizeHandleTL: {
		position: "absolute",
		width: 20,
		height: 20,
		top: 0,
		left: 0,
		cursor: "nwse-resize",
		zIndex: 10,
	},
	resizeHandleTR: {
		position: "absolute",
		width: 20,
		height: 20,
		top: 0,
		right: 0,
		cursor: "nesw-resize",
		zIndex: 10,
	},
}));

const formatTime = (seconds) => {
	if (!seconds || isNaN(seconds)) return "0:00";
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const GlobalAudioPlayer = () => {
	const classes = useStyles();
	const location = useLocation();
	const {
		audioRef,
		seekTimeRef,
		currentAudio,
		isPlaying,
		setIsPlaying,
		audioRate,
		stopAudio,
		changeRate,
	} = useContext(AudioPlayerContext);

	const ticketMatch = location.pathname.match(/^\/tickets\/([^/]+)/);
	const currentViewTicketUuid = ticketMatch ? ticketMatch[1] : null;
	const originTicketUuid = currentAudio?.ticketUuid;
	const isOnOriginTicket = originTicketUuid && currentViewTicketUuid
		&& originTicketUuid === currentViewTicketUuid;

	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [seeking, setSeeking] = useState(false);
	const [muted, setMuted] = useState(false);
	const [showBigPlay, setShowBigPlay] = useState(true);
	const [controlsVisible, setControlsVisible] = useState(false);
	const animFrameRef = useRef(null);
	const hideTimerRef = useRef(null);

	// Drag state
	const dragRef = useRef(null);
	const [position, setPosition] = useState(null);
	const dragging = useRef(false);
	const dragOffset = useRef({ x: 0, y: 0 });

	// Resize state
	const [size, setSize] = useState({ w: DEFAULT_VIDEO_W, h: DEFAULT_VIDEO_H });
	const resizing = useRef(false);
	const resizeCorner = useRef(null);
	const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0, left: 0, top: 0 });

	// Audio setup effect
	useEffect(() => {
		if (!currentAudio || !audioRef.current || currentAudio.mediaType === "video") return;
		const audio = audioRef.current;

		const onLoadedMetadata = () => {
			setDuration(audio.duration);
			audio.playbackRate = audioRate;
			audio.play().catch(() => {});
		};
		const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
		const onPause = () => setIsPlaying(false);
		const onPlay = () => setIsPlaying(true);

		audio.addEventListener("loadedmetadata", onLoadedMetadata);
		audio.addEventListener("ended", onEnded);
		audio.addEventListener("pause", onPause);
		audio.addEventListener("play", onPlay);

		if (audio.readyState >= 1) {
			setDuration(audio.duration);
			audio.playbackRate = audioRate;
			audio.play().catch(() => {});
		}

		return () => {
			audio.removeEventListener("loadedmetadata", onLoadedMetadata);
			audio.removeEventListener("ended", onEnded);
			audio.removeEventListener("pause", onPause);
			audio.removeEventListener("play", onPlay);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentAudio]);

	// Video setup effect
	useEffect(() => {
		if (!currentAudio || currentAudio.mediaType !== "video") return;
		setPosition(null);
		setSize({ w: DEFAULT_VIDEO_W, h: DEFAULT_VIDEO_H });
		setCurrentTime(0);
		setDuration(0);
		setShowBigPlay(true);
		setMuted(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentAudio]);

	// Time sync
	useEffect(() => {
		const update = () => {
			if (audioRef.current && !seeking) {
				setCurrentTime(audioRef.current.currentTime);
				const dur = audioRef.current.duration;
				if (dur && isFinite(dur)) setDuration(dur);
			}
			animFrameRef.current = requestAnimationFrame(update);
		};
		animFrameRef.current = requestAnimationFrame(update);
		return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
	}, [audioRef, seeking]);

	// --- Drag ---
	const handleDragStart = useCallback((e) => {
		if (resizing.current) return;
		dragging.current = true;
		const rect = dragRef.current.getBoundingClientRect();
		dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
		document.addEventListener("mousemove", handleDragMove);
		document.addEventListener("mouseup", handleDragEnd);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDragMove = useCallback((e) => {
		if (!dragging.current) return;
		setPosition({ left: e.clientX - dragOffset.current.x, top: e.clientY - dragOffset.current.y });
	}, []);

	const handleDragEnd = useCallback(() => {
		dragging.current = false;
		document.removeEventListener("mousemove", handleDragMove);
		document.removeEventListener("mouseup", handleDragEnd);
	}, [handleDragMove]);

	// --- Resize ---
	const handleResizeStart = useCallback((corner) => (e) => {
		e.stopPropagation();
		e.preventDefault();
		resizing.current = true;
		resizeCorner.current = corner;
		const rect = dragRef.current.getBoundingClientRect();
		resizeStart.current = { x: e.clientX, y: e.clientY, w: rect.width, h: rect.height, left: rect.left, top: rect.top };
		document.addEventListener("mousemove", handleResizeMove);
		document.addEventListener("mouseup", handleResizeEnd);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleResizeMove = useCallback((e) => {
		if (!resizing.current) return;
		const dx = e.clientX - resizeStart.current.x;
		const dy = e.clientY - resizeStart.current.y;
		const corner = resizeCorner.current;
		const s = resizeStart.current;
		let newW = s.w, newH = s.h, newLeft = null, newTop = null;

		if (corner === "br") {
			newW = Math.max(MIN_VIDEO_W, s.w + dx);
			newH = Math.max(MIN_VIDEO_H, s.h + dy);
		} else if (corner === "bl") {
			newW = Math.max(MIN_VIDEO_W, s.w - dx);
			newH = Math.max(MIN_VIDEO_H, s.h + dy);
			newLeft = s.left + (s.w - newW); newTop = s.top;
		} else if (corner === "tl") {
			newW = Math.max(MIN_VIDEO_W, s.w - dx);
			newH = Math.max(MIN_VIDEO_H, s.h - dy);
			newLeft = s.left + (s.w - newW); newTop = s.top + (s.h - newH);
		} else if (corner === "tr") {
			newW = Math.max(MIN_VIDEO_W, s.w + dx);
			newH = Math.max(MIN_VIDEO_H, s.h - dy);
			newTop = s.top + (s.h - newH); newLeft = s.left;
		}

		setSize({ w: newW, h: newH });
		if (newLeft !== null || newTop !== null) {
			setPosition((prev) => ({
				left: newLeft !== null ? newLeft : (prev ? prev.left : s.left),
				top: newTop !== null ? newTop : (prev ? prev.top : s.top),
			}));
		}
	}, []);

	const handleResizeEnd = useCallback(() => {
		resizing.current = false;
		document.removeEventListener("mousemove", handleResizeMove);
		document.removeEventListener("mouseup", handleResizeEnd);
	}, [handleResizeMove]);

	const handleResetSize = useCallback(() => {
		setSize({ w: DEFAULT_VIDEO_W, h: DEFAULT_VIDEO_H });
	}, []);

	// --- Controls show/hide ---
	const showControlsTimer = useCallback(() => {
		setControlsVisible(true);
		if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
		hideTimerRef.current = setTimeout(() => {
			if (!seeking) setControlsVisible(false);
		}, 3000);
	}, [seeking]);

	if (!currentAudio) return null;

	const handlePlayPause = () => {
		if (!audioRef.current) return;
		if (audioRef.current.paused) {
			audioRef.current.play().catch(() => {});
		} else {
			audioRef.current.pause();
		}
	};

	const handleSliderChange = (_, value) => {
		setSeeking(true);
		setCurrentTime(value);
	};

	const handleSliderCommitted = (_, value) => {
		if (audioRef.current) audioRef.current.currentTime = value;
		setSeeking(false);
	};

	const toggleMute = () => {
		if (audioRef.current) {
			audioRef.current.muted = !audioRef.current.muted;
			setMuted(audioRef.current.muted);
		}
	};

	const handleFullscreen = () => {
		if (audioRef.current) {
			if (audioRef.current.requestFullscreen) {
				audioRef.current.requestFullscreen();
			} else if (audioRef.current.webkitEnterFullscreen) {
				audioRef.current.webkitEnterFullscreen();
			}
		}
	};

	const hidden = isOnOriginTicket;

	// ==================== VIDEO PLAYER ====================
	if (currentAudio.mediaType === "video") {
		if (hidden) return null;

		const posStyle = position
			? { left: position.left, top: position.top, bottom: "auto", right: "auto" }
			: { bottom: 16, right: 16 };

		const alwaysShowControls = !isPlaying || controlsVisible;

		return (
			<div
				ref={dragRef}
				className={classes.videoRoot}
				style={{ ...posStyle, width: size.w, height: size.h }}
			>
				{/* ---- Header ---- */}
				<div className={classes.videoHeader} onMouseDown={handleDragStart}>
					<div className={classes.videoHeaderLeft}>
						<VideocamIcon className={classes.videoHeaderIcon} />
						<Typography className={classes.videoHeaderName} title={currentAudio.contactName || ""}>
							{currentAudio.contactName || "Vídeo"}
						</Typography>
					</div>
					<div className={classes.videoHeaderActions}>
						<IconButton
							className={classes.headerBtn}
							size="small"
							onClick={(e) => { e.stopPropagation(); handleResetSize(); }}
							title="Tamanho padrão"
						>
							<AspectRatioIcon style={{ fontSize: 18 }} />
						</IconButton>
						<IconButton
							className={classes.headerBtnClose}
							size="small"
							onClick={(e) => { e.stopPropagation(); stopAudio(); }}
							title="Fechar"
						>
							<CloseIcon style={{ fontSize: 18 }} />
						</IconButton>
					</div>
				</div>

				{/* ---- Video body ---- */}
				<div
					className={classes.videoBody}
					onMouseEnter={showControlsTimer}
					onMouseMove={showControlsTimer}
					onMouseLeave={() => { if (!seeking) setControlsVisible(false); }}
				>
					<video
						key={currentAudio.sourceUrl}
						ref={audioRef}
						className={classes.videoElement}
						src={currentAudio.sourceUrl}
						preload="auto"
						playsInline
						onClick={handlePlayPause}
						onLoadedMetadata={(e) => {
							if (seekTimeRef && seekTimeRef.current !== null) {
								e.target.currentTime = seekTimeRef.current;
								seekTimeRef.current = null;
							}
							e.target.play().catch(() => {});
						}}
						onPlay={() => { setIsPlaying(true); setShowBigPlay(false); }}
						onPause={() => { setIsPlaying(false); setShowBigPlay(true); }}
						onEnded={() => { setIsPlaying(false); setShowBigPlay(true); }}
					/>

					{/* Big center play */}
					{showBigPlay && (
						<div className={classes.videoBigPlay} onClick={handlePlayPause}>
							<PlayArrowIcon />
						</div>
					)}

					{/* Controls overlay */}
					<div
						className={classes.videoControlsOverlay}
						style={alwaysShowControls ? { opacity: 1 } : undefined}
					>
						{/* Progress */}
						<div className={classes.videoProgressRow}>
							<Typography className={classes.videoTimeText}>
								{formatTime(currentTime)}
							</Typography>
							<Slider
								className={classes.videoProgressSlider}
								min={0}
								max={duration || 1}
								value={currentTime}
								onChange={handleSliderChange}
								onChangeCommitted={handleSliderCommitted}
								step={0.1}
							/>
							<Typography className={classes.videoTimeText}>
								{formatTime(duration)}
							</Typography>
						</div>

						{/* Buttons */}
						<div className={classes.videoControlsRow}>
							<div className={classes.videoControlsLeft}>
								<IconButton className={classes.controlBtn} size="small" onClick={handlePlayPause}>
									{isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
								</IconButton>
								<IconButton className={classes.controlBtn} size="small" onClick={toggleMute}>
									{muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
								</IconButton>
							</div>
							<div className={classes.videoControlsRight}>
								<IconButton
									className={classes.controlBtn}
									size="small"
									onClick={() => {
										const link = document.createElement("a");
										link.href = currentAudio.sourceUrl;
										link.download = "video.mp4";
										link.click();
									}}
									title="Baixar"
								>
									<GetAppIcon />
								</IconButton>
								<IconButton className={classes.controlBtn} size="small" onClick={handleFullscreen} title="Tela cheia">
									<FullscreenIcon />
								</IconButton>
							</div>
						</div>
					</div>

					{/* Resize handles */}
					<div className={classes.resizeHandle} onMouseDown={handleResizeStart("br")} />
					<div className={classes.resizeHandleBL} onMouseDown={handleResizeStart("bl")} />
					<div className={classes.resizeHandleTL} onMouseDown={handleResizeStart("tl")} />
					<div className={classes.resizeHandleTR} onMouseDown={handleResizeStart("tr")} />
				</div>
			</div>
		);
	}

	// ==================== AUDIO PLAYER ====================
	return (
		<div className={classes.audioRoot} style={hidden ? { display: "none" } : undefined}>
			<audio
				key={currentAudio.sourceUrl}
				ref={audioRef}
				src={currentAudio.sourceUrl}
				preload="metadata"
			/>

			<MicIcon className={classes.micIcon} />

			{currentAudio.contactName && (
				<Typography className={classes.contactName} title={currentAudio.contactName}>
					{currentAudio.contactName}
				</Typography>
			)}

			<IconButton size="small" onClick={handlePlayPause}>
				{isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
			</IconButton>

			<Slider
				className={classes.slider}
				min={0}
				max={duration || 1}
				value={currentTime}
				onChange={handleSliderChange}
				onChangeCommitted={handleSliderCommitted}
				step={0.1}
			/>

			<Typography className={classes.timeText}>
				{formatTime(currentTime)} / {formatTime(duration)}
			</Typography>

			<Button
				className={classes.rateButton}
				onClick={changeRate}
				size="small"
				disableRipple
			>
				{audioRate}x
			</Button>

			<IconButton className={classes.closeButton} size="small" onClick={stopAudio}>
				<CloseIcon fontSize="small" />
			</IconButton>
		</div>
	);
};

export default GlobalAudioPlayer;
